import { useState, useEffect } from 'react';
import { Vendor } from '@/types';
import { fetchVendors } from '@/services/api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface VendorSelectProps {
  onVendorSelect: (vendor: Vendor | null) => void;
  disabled?: boolean;
  selectedVendor?: Vendor | null;
  preSelectedVendorId?: string;
}

const VendorSelect = ({ onVendorSelect, disabled = false, selectedVendor, preSelectedVendorId }: VendorSelectProps) => {
  const selectProps = (selectedVendor ? { value: selectedVendor.vendorId } : {})
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadVendors = async () => {
      try {
        setLoading(true);
        setError(null);
        const vendorsData = await fetchVendors();

        if (vendorsData && Array.isArray(vendorsData)) {
          setVendors(vendorsData);
          if (preSelectedVendorId) {
            const selectedVendor = vendorsData.find(vendor => vendor.vendorId === preSelectedVendorId) || null;
            onVendorSelect(selectedVendor);
          }
        } else {
          console.error('Invalid vendor data structure:', vendorsData);
          setError('Invalid vendor data received. Please try again later.');
          setVendors([]);
        }
      } catch (err) {
        setError('Failed to load vendors. Please try again later.');
        console.error('Error loading vendors:', err);
        setVendors([]);
      } finally {
        setLoading(false);
      }
    };

    loadVendors();
  }, [preSelectedVendorId]);

  const handleSelectChange = (vendorId: string) => {
    if (preSelectedVendorId) {
      const selectedVendor = vendors.find(vendor => vendor.vendorId === preSelectedVendorId) || null;
      onVendorSelect(selectedVendor);
    } else {
      const selectedVendor = vendors.find(vendor => vendor.vendorId === vendorId) || null;
      onVendorSelect(selectedVendor);
    }
  };

  return (
    <div className="space-y-2">
      <Label htmlFor="vendor-select" className="text-sm font-medium">
        Vendor Name
      </Label>

      {error ? (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : (
        <Select

          onValueChange={handleSelectChange}
          disabled={disabled || loading}
          {...selectProps}
        >
          <SelectTrigger id="vendor-select" className="w-full">
            <SelectValue placeholder={loading ? "Loading vendors..." : "Select a vendor"} />
          </SelectTrigger>
          <SelectContent>
            {Array.isArray(vendors) && vendors.map(vendor => (
              <SelectItem key={vendor.vendorId} value={vendor.vendorId}>
                <div className="flex items-center gap-2">
                  {vendor.vendorLogoPath && (
                    <img
                      src={vendor.vendorLogoPath}
                      alt={vendor.vendorDisplayName}
                      className="w-6 h-6 object-contain rounded"
                      onError={(e) => {
                        // Hide image if it fails to load
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  )}
                  <span>{vendor.vendorDisplayName}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
};

export default VendorSelect;
